// The routes of 11 Oct, frozen for the `os` and `legacy` flags (shell-ia §17): `os` mounts them in the Agentic OS frame with the launcher
// at `/`; `legacy` in the pre-OS shell with the old home page. Every page loads lazily, so none of them weighs on the ECONIC bundle
// (shell-ia §18). Deleted with the flags on 29 Oct. WP-SHELL.
import { lazy, Suspense, type ComponentType, type ReactNode } from "react";
import { Navigate, Outlet, useLocation, type RouteObject } from "react-router";
import { permissionMatches, useAuth, type AuthUser } from "@ncp/api-client";
import { ForbiddenPage } from "../pages/ForbiddenPage";
import { RootLayout, WorkspaceLayout } from "./layouts";
import { WorkspaceEntry } from "./WorkspaceEntry";
import { useWorkspace } from "./workspace-context";

const page = <M,>(load: () => Promise<M>, name: keyof M & string) => {
  const C = lazy(() => load().then((m) => ({ default: m[name] as unknown as ComponentType })));
  return (props: Record<string, unknown> = {}): ReactNode => (
    <Suspense fallback={null}>
      <C {...props} />
    </Suspense>
  );
};

// A built-in screen loads with its chunk (./ModulePage.tsx), not with the frame.
const ModulePage = page(() => import("./ModulePage"), "ModulePage");
// The sign-in is the strict one under every flag (shell-ia §17: no flag brings back persona sign-in).
const LandingPage = page(() => import("../econic/login/landing"), "LandingPage");
const HomePage = page(() => import("../pages/HomePage"), "HomePage");
const LauncherPage = page(() => import("../launcher/LauncherPage"), "LauncherPage");
const NewChatPage = page(() => import("../pages/ChatPage"), "NewChatPage");
const ChatPage = page(() => import("../pages/ChatPage"), "ChatPage");
const InboxPage = page(() => import("../pages/InboxPage"), "InboxPage");
const ArtifactsPage = page(() => import("../pages/ArtifactsPage"), "ArtifactsPage");
const KnowledgePage = page(() => import("../pages/KnowledgePage"), "KnowledgePage");
const MattersPage = page(() => import("../pages/MattersPage"), "MattersPage");
const MatterPage = page(() => import("../pages/MatterPage"), "MatterPage");
const DecisionBriefPage = page(() => import("../pages/DecisionBriefPage"), "DecisionBriefPage");
const WatchersPage = page(() => import("../pages/WatchersPage"), "WatchersPage");
const CustomPage = page(() => import("../pages/CustomPage"), "CustomPage");
const MyPagesPage = page(() => import("../pages/MyPagesPage"), "MyPagesPage");
const MyPagePage = page(() => import("../pages/MyPagePage"), "MyPagePage");

/** Identities that may enter the workspace: staff whose roles grant `assistant.use` (platform administrators always may). */
function hasWorkspaceAccess(user: AuthUser): boolean {
  if (user.kind !== "Staff") return false;
  return user.roles.includes("Admin") || permissionMatches(user.permissions, "assistant.use");
}

/** Staff holding `assistant.use` may enter; everyone else is sent to sign in. */
function RequireWorkspaceAccess() {
  const { isAuthenticated, user } = useAuth();
  const { pathname, search, hash } = useLocation();
  if (!isAuthenticated || !user) return <Navigate to="/login" replace state={{ from: `${pathname}${search}${hash}` }} />;
  if (!hasWorkspaceAccess(user)) return <ForbiddenPage permission="assistant.use" />;
  return <Outlet />;
}

/** Guards a route behind an `assistant.*` / `knowledge.*` permission resolved from the session or the catalog. */
export function RequirePermission({ permission }: { permission: string }) {
  const { can, loading } = useWorkspace();
  if (loading) return null;
  if (!can(permission)) return <ForbiddenPage permission={permission} />;
  return <Outlet />;
}

export function frozenRoutes(shell: "os" | "legacy"): RouteObject[] {
  return [
    {
      element: <RootLayout />,
      children: [
        { path: "/login", element: <LandingPage /> },
        { path: "/mfa", element: <LandingPage /> },
        { path: "/password", element: <LandingPage /> },
        {
          element: <RequireWorkspaceAccess />,
          children: [
            {
              element: <WorkspaceLayout />,
              children: [
                { index: true, element: shell === "legacy" ? <HomePage /> : <LauncherPage /> },
                { path: "chat", element: <NewChatPage /> },
                { path: "chat/:threadId", element: <ChatPage /> },
                { path: "inbox", element: <InboxPage /> },
                { element: <RequirePermission permission="matters.read" />, children: [{ path: "matters", element: <MattersPage /> }, { path: "matters/:matterId", element: <MatterPage /> }, { path: "matters/:matterId/decision", element: <DecisionBriefPage /> }] },
                { element: <RequirePermission permission="watchers.use" />, children: [{ path: "watchers", element: <WatchersPage /> }] },
                { path: "artifacts", element: <ArtifactsPage /> },
                { path: "artifacts/:artifactId", element: <ArtifactsPage /> },
                { element: <RequirePermission permission="knowledge.read" />, children: [{ path: "knowledge", element: <KnowledgePage /> }, { path: "knowledge/:collectionKey", element: <KnowledgePage /> }, { path: "knowledge/:collectionKey/:documentId", element: <KnowledgePage /> }] },
                { path: "evidence", element: <ModulePage page="evidence" /> },
                { path: "tracker", element: <ModulePage page="tracker" /> },
                { path: "library", element: <ModulePage page="library" /> },
                { path: "p/:pageKey/*", element: <CustomPage /> },
                { path: "my-pages", element: <MyPagesPage /> },
                { path: "my-pages/:key", element: <MyPagePage /> },
                { path: "w/:workspaceKey/*", element: <WorkspaceEntry /> },
                { path: "*", element: <Navigate to="/" replace /> },
              ],
            },
          ],
        },
      ],
    },
  ];
}
